import React, { useEffect, useState } from 'react';
import {
  BellAlertIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid';
import { Spin, message as Message } from 'antd';
import { LoadingIcon } from '../../../components/Spinner';
import { menuUrl } from '../../../../env';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../rootReducer';
import { TTUserProfile } from '../../../../interfaces/user';
import { LoginPopUp } from '../../../LogInPopUp';
import moment from 'moment';

type Props = {
  message: string;
  restaurant_name: string;
  menu_item_id?: number;
  merchant_id?: number;
  qr_code_id?: string;
};

const OrderingDisabledMessage: React.FC<Props> = ({
  message,
  restaurant_name,
  menu_item_id,
  merchant_id,
  qr_code_id,
}) => {
  const [loading, setLoading] = useState(false);
  const loggedIn = useSelector<RootState, boolean>(
    state => state.auth.isLoggedIn,
  );
  const guestProfile = useSelector<RootState, TTUserProfile | null>(
    state => state.auth.userProfile,
  );
  // const [recentlyAddedAt, setRecentlyAddedAt] = useState(
  //   localStorage.getItem(`__tt_order_reminder_${merchant_id}`),
  // );
  const [recentlyAdded, setRecentlyAdded] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const handleReminder = async (guest: TTUserProfile) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${menuUrl}/api/v1/guests/ordering-reminder`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            guest_id: guest?.id,
            phone_number: guest?.phone_number,
            menu_item_id: menu_item_id,
            merchant_id: merchant_id,
            unique_qr_code_id: qr_code_id,
          }),
        },
      );
      localStorage.setItem(`__tt_order_reminder_${merchant_id}`, 'true');
      localStorage.setItem(
        `__tt_order_reminder_added_${merchant_id}`,
        moment().toISOString(),
      );
      setRecentlyAdded(true);
      const event = new Event('recentlyAddedChanged');
      window.dispatchEvent(event);
      Message.success(
        'Successfully saved! We will text you when ordering turned back on.',
      );
    } catch (e) {
      Message.error('Unable to create a reminder!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const recentlyAddedKey = `__tt_order_reminder_added_${merchant_id}`;

    // Function to update state based on localStorage
    const updateRecentlyAdded = () => {
      const storedTime = localStorage.getItem(recentlyAddedKey);
      if (storedTime !== null) {
        const currentTime = moment();
        const timeStored = moment(storedTime);
        if (currentTime.diff(timeStored, 'hours') < 48) {
          setRecentlyAdded(true);
        } else {
          setRecentlyAdded(false);
        }
      }
    };

    // Initially set state
    updateRecentlyAdded();

    // Listen for changes to localStorage
    const handleStorageChange = (e: StorageEvent) => {
      updateRecentlyAdded();
    };

    window.addEventListener('recentlyAddedChanged', handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener('recentlyAddedChanged', handleStorageChange);
    };
  }, [merchant_id]); // If merchant_id changes, re-attach event listener

  return (
    <>
      <Spin spinning={loading} indicator={LoadingIcon}>
        <div className="rounded-md bg-secondary-container p-4 mx-4 my-4 md:my-0">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon
                className="h-5 w-5 text-on-secondary-container"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-on-secondary-container">{`Message from ${restaurant_name}`}</h3>
              <div className="mt-2 text-sm text-on-secondary-container">
                <p>{message}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={async () => {
              if (loggedIn && guestProfile) {
                await handleReminder(guestProfile);
              } else {
                setShowAuth(true);
              }
            }}
            className="bg-primary text-on-primary px-4 py-1 rounded-full flex flex-row items-center mt-2 ml-6"
          >
            {recentlyAdded ? (
              <>
                <CheckIcon className="w-4 h-4 text-on-primary mr-2" />
                Notification added!
              </>
            ) : (
              <>
                <BellAlertIcon className="w-4 h-4 text-on-primary mr-2" />
                Notify me when ordering is back on
              </>
            )}
          </button>
        </div>
      </Spin>
      {showAuth && (
        <LoginPopUp
          title="Enter your phone number for a reminder"
          onSuccess={async (profile: TTUserProfile) => {
            setTimeout(async () => {
              setShowAuth(false);
              await handleReminder(profile);
            }, 20);
          }}
          onCancel={() => {
            setShowAuth(false);
          }}
        />
      )}
    </>
  );
};

export default OrderingDisabledMessage;
